// Copyright 2026. WebPros International GmbH. All rights reserved.

import * as React from 'react';
import { connect } from 'react-redux';
import { TASKS } from 'admin/computeResource/constants/tests';
import * as taskActions from 'admin/task/actions';
import { TASKS_TABLE } from 'admin/task/constants/test';
import {
    ITaskListFilters,
    ITaskResponse,
} from 'common/api/resources/Task';
import { EmptyView } from 'common/components/EmptyView/EmptyView';
import { TaskStatus } from 'admin/task/components/TaskStatus';
import ComputeResourceTaskDialog from 'admin/task/containers/TaskDialog';
import { LOADING_FLAGS } from 'common/modules/app/loadingFlags/constants';
import { formatTableDateUtc } from 'common/date';
import CopyText from 'common/containers/CopyText/CopyText';
import { RootState } from 'admin/core/store';
import {
    bindActionCreators,
    Dispatch,
} from 'redux';
import {
    Action,
    Translate,
} from '@plesk/ui-library';
import { ICONS } from 'common/constants';
import List from 'common/components/List/List';
import { getActionColumnProps } from 'common/helpers/list';
import { Link } from 'react-router-dom';
import { pathTo } from 'common/helpers/core';
import { TruncateText } from 'common/components/TruncateText/TruncateText';
import { ActionsCell } from 'common/components/ActionsCell/ActionsCell';

const columns = [{
    key: 'colId',
    width: '1%',
    title: <Translate content="task.list.id" />,
}, {
    key: 'colAction',
    title: <Translate content="task.list.queue" />,
}, {
    key: 'colStatus',
    title: <Translate content="task.list.status" />,
}, {
    key: 'colUser',
    title: <Translate content="task.list.user" />,
}, {
    key: 'colComputeResource',
    title: <Translate content="task.list.computeResource" />,
}, {
    key: 'colServer',
    title: <Translate content="task.list.server" />,
}, {
    key: 'colDates',
    title: <Translate content="task.list.dates" />,
}, {
    key: 'colDuration',
    title: <Translate content="task.list.duration" />,
}, getActionColumnProps({ width: '5%' }),
];

interface ITaskTableProps {
    excludedColumns?: string[];
    isFirstLoading: boolean;
    borderless?: boolean;
    filters?: ITaskListFilters;
    toolbar?: React.ReactElement;
}

export type TaskTableProps =
    ITaskTableProps &
    ReturnType<typeof mapStateToProps> &
    ReturnType<typeof mapDispatchToProps>;

export const TaskTable: React.FC<TaskTableProps> = ({
    tasks,
    isFirstLoading,
    borderless,
    taskActions: {
        getTaskList,
        setTask,
        retry,
        cancel,
    },
    isLoadingTaskList,
    excludedColumns,
    filters,
    toolbar,
}) => {
    const [isOpen, setIsOpen] = React.useState(false);

    const handleCancel = (id: number) => () => cancel(id);
    const handleRetry = (id: number) => () => retry(id);
    const handleOpen = (task: ITaskResponse) => () => {
        setTask(task);
        setIsOpen(true);
    };
    const handleClose = () => setIsOpen(false);

    const loadPaginated = (page: number) => getTaskList({
        filters,
        page,
    });

    const data = tasks.data.map((item) => ({
        colId: item.id,
        colAction: <b>{item.action}</b>,
        colStatus: <TaskStatus item={item} onOpen={handleOpen(item)} />,
        colComputeResource: (
            item.compute_resource && (
                <>
                    <Action
                        component={Link}
                        to={pathTo(`compute_resources/${item.compute_resource.id}`)}
                    >
                        {item.compute_resource.name}
                    </Action>

                    <div>
                        {item.compute_resource.host}
                    </div>
                </>
            )
        ),
        colServer: (
            <>
                {item.compute_resource_vm_id && (
                    <Action
                        component={Link}
                        to={pathTo(`servers/${item.compute_resource_vm_id}`)}
                    >
                        {item.compute_resource_vm_id}
                    </Action>
                )}
                {item.compute_resource_vm_uuid && (
                    <CopyText text={item.compute_resource_vm_uuid}>
                        <TruncateText text={item.compute_resource_vm_uuid} maxLength={12} />
                    </CopyText>
                )}
            </>
        ),
        colUser: item.user_email,
        colDuration: <Translate content="task.list.durationValue" params={{ value: item.duration }}/>,
        colDates: (
            <>
                <div>{formatTableDateUtc(item.started_at)}</div>
                {item.finished_at ? <div>{formatTableDateUtc(item.finished_at)}</div> : null}
            </>
        ),
        colActions: (
            <ActionsCell
                triggerTooltip={<Translate content="task.list.actions.menu" />}
                actions={[
                    {
                        icon: 'reload',
                        label: <Translate content="task.tooltip.retry" />,
                        tooltip: <Translate content="task.tooltip.retry" />,
                        disabled: !item.is_retryable,
                        onClick: handleRetry(item.id),
                        'data-cy': TASKS_TABLE.RETRY_BUTTON,
                    },
                    {
                        icon: 'recycle',
                        label: <Translate content="task.cancelPopover.button" />,
                        disabled: !item.is_cancelable,
                        'data-cy': TASKS_TABLE.CANCEL_BUTTON,
                        confirmation: {
                            handleConfirm: handleCancel(item.id),
                            button: <Translate content="task.cancelPopover.button" />,
                            title: <Translate content="task.cancelPopover.title" />,
                        },
                    },
                ]}
            />
        ),
        key: item.id.toString(),
    }));

    const filtered = filters !== undefined && Object.values(filters).some(filter => !!filter);

    return (
        <>
            <List
                isLoading={isLoadingTaskList}
                isFirstLoading={isFirstLoading}
                loadItems={loadPaginated}
                borderless={borderless}
                verticalAlign="top"
                meta={tasks.meta}
                filtered={filtered || isLoadingTaskList}
                toolbar={toolbar}
                emptyView={
                    <EmptyView
                        title="task.emptyView.title"
                        icon={ICONS.TASKS}
                    />
                }
                data-cy={TASKS.TABLE}
                sortColumn="colId"
                sortDirection="DESC"
                columns={columns.filter(
                    (column) => !excludedColumns || !excludedColumns.includes(column.key)
                )}
                data={data}
            />
            <ComputeResourceTaskDialog handleClose={handleClose} isOpen={isOpen} />
        </>
    );
};

const mapStateToProps = (state: RootState) => ({
    tasks: state.task.list,
    isLoadingTaskList: state.app.loadingFlags.has(LOADING_FLAGS.COMPUTE_RESOURCE_TASK_LIST),
});

const mapDispatchToProps = (dispatch: Dispatch) => ({
    taskActions: bindActionCreators(taskActions, dispatch),
});

export default connect(mapStateToProps, mapDispatchToProps)(TaskTable);
